import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
  Plus, Copy, RefreshCw, ExternalLink, Search, X,
  Sparkles, AlertTriangle, CheckCircle2, Clock, Archive,
} from "lucide-react";
import api from "../../lib/api";

/**
 * Brief 3.4 §2.7 — Operations dashboard for the magic-link flow.
 *
 * Three jobs:
 *   1. Mint new stub merchant + onboarding link (calls POST /admin/merchants/stub)
 *   2. List every token we've ever minted, with the four-state pill
 *      (active · used · superseded · expired) and copy-to-clipboard
 *   3. Regenerate a link if the merchant lost it (calls
 *      POST /admin/merchants/{id}/regenerate-onboarding-link)
 *
 * Used during the SIM-warming window for UAT — proves the four-state
 * /onboard/:token UX with real data before merchants arrive. Once
 * Brief 3.4.2 (Telegram group creation) ships, this page becomes the
 * fallback for ops to mint links manually outside the Telegram flow.
 */
const STATUS_FILTERS = [
  { key: "all", label: "All" },
  { key: "active", label: "Active" },
  { key: "used", label: "Used" },
  { key: "expired", label: "Expired" },
  { key: "superseded", label: "Superseded" },
];

export default function AdminOnboardingLinks() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [search, setSearch] = useState("");
  const [showMint, setShowMint] = useState(false);
  const [toast, setToast] = useState(null);

  const refresh = async (next = statusFilter) => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/onboarding-links", {
        params: { status: next },
      });
      setData(data);
      setError("");
    } catch (e) {
      setError(e?.response?.data?.detail || "Could not load onboarding links.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { refresh(statusFilter); /* eslint-disable-next-line */ }, [statusFilter]);

  const showToast = (message, kind = "ok") => {
    setToast({ message, kind });
    setTimeout(() => setToast(null), 3500);
  };

  const onCopy = async (link) => {
    try {
      await navigator.clipboard.writeText(link.magic_link_url);
      showToast(`Copied magic link for ${link.legal_name || link.ref}`);
    } catch {
      showToast("Couldn't copy — please copy manually", "warn");
    }
  };

  const onRegenerate = async (link) => {
    if (!window.confirm(
      `Regenerate the onboarding link for ${link.legal_name}?\n\n` +
      "The current link will be invalidated immediately. The new link is valid for 7 days."
    )) return;
    try {
      const { data: fresh } = await api.post(
        `/admin/merchants/${link.merchant_id}/regenerate-onboarding-link`,
      );
      await navigator.clipboard.writeText(fresh.magic_link_url).catch(() => {});
      showToast(`Regenerated. New link copied to clipboard.`);
      refresh();
    } catch (e) {
      const detail = e?.response?.data?.detail;
      const msg = (detail && typeof detail === "object" ? detail.message : detail) || "Couldn't regenerate.";
      showToast(msg, "warn");
    }
  };

  const filtered = (data?.links || []).filter((l) => {
    if (!search.trim()) return true;
    const q = search.toLowerCase().trim();
    return [l.legal_name, l.ref, l.primary_contact_email, l.primary_contact_name, l.merchant_id]
      .filter(Boolean).some((v) => String(v).toLowerCase().includes(q));
  });

  return (
    <div data-testid="admin-onboarding-links" className="max-w-7xl">
      {/* Header */}
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <div className="text-[11px] uppercase tracking-[0.22em] text-zinc-500">— Onboarding</div>
          <h1 className="font-display mt-2 text-3xl sm:text-4xl font-semibold tracking-tighter chrome-text leading-tight">
            Magic links
          </h1>
          <p className="mt-2 text-[13px] text-zinc-400 max-w-2xl">
            Mint pre-signup merchants, track outstanding invitations, and regenerate links the merchant has lost.
            Each link is single-use, expires in 7 days, and lands the merchant on a pre-filled signup form.
          </p>
        </div>
        <button
          type="button"
          onClick={() => setShowMint(true)}
          data-testid="onboarding-links-mint-btn"
          className="group inline-flex items-center gap-1.5 rounded-full bg-white text-black hover:bg-zinc-200 transition-colors px-5 py-2.5 text-[13px] font-medium"
        >
          <Plus className="h-3.5 w-3.5" />
          New link
        </button>
      </div>

      {/* Counts */}
      {data && (
        <div data-testid="onboarding-links-counts" className="mt-7 grid grid-cols-2 sm:grid-cols-5 gap-3">
          <CountCard label="Total" value={data.counts.total} />
          <CountCard label="Active" value={data.counts.active} accent="cyan" />
          <CountCard label="Used" value={data.counts.used} accent="emerald" />
          <CountCard label="Expired" value={data.counts.expired} accent="amber" />
          <CountCard label="Superseded" value={data.counts.superseded} accent="zinc" />
        </div>
      )}

      {/* Filters */}
      <div className="mt-6 flex flex-wrap items-center gap-3">
        <div className="flex gap-1 rounded-full border border-white/[0.06] bg-white/[0.015] p-1" data-testid="onboarding-links-filter">
          {STATUS_FILTERS.map(({ key, label }) => (
            <button
              key={key}
              type="button"
              onClick={() => setStatusFilter(key)}
              data-testid={`onboarding-links-filter-${key}`}
              className={`px-3.5 py-1.5 rounded-full text-[12px] transition-colors ${
                statusFilter === key
                  ? "bg-white/[0.08] text-white"
                  : "text-zinc-400 hover:text-zinc-200"
              }`}
            >
              {label}
            </button>
          ))}
        </div>
        <div className="relative flex-1 max-w-sm">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-zinc-500" />
          <input
            type="search"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search by name, email, ref…"
            data-testid="onboarding-links-search"
            className="w-full pl-9 pr-3 py-2 rounded-full border border-white/[0.06] bg-white/[0.015] focus:border-[#00E5FF]/40 focus:outline-none focus:ring-2 focus:ring-[#00E5FF]/20 text-[13px] text-white placeholder:text-zinc-600"
          />
        </div>
      </div>

      {/* Table */}
      <div className="mt-5 rounded-2xl border border-white/[0.06] bg-white/[0.015] overflow-hidden">
        <table className="w-full" data-testid="onboarding-links-table">
          <thead className="bg-white/[0.02] text-[10.5px] uppercase tracking-[0.18em] text-zinc-500">
            <tr>
              <th className="text-left px-4 py-3 font-normal">Merchant</th>
              <th className="text-left px-4 py-3 font-normal">Contact</th>
              <th className="text-left px-4 py-3 font-normal">Status</th>
              <th className="text-left px-4 py-3 font-normal">Issued</th>
              <th className="text-left px-4 py-3 font-normal">Expires</th>
              <th className="text-right px-4 py-3 font-normal">Actions</th>
            </tr>
          </thead>
          <tbody className="text-[13px]">
            {loading && (
              <tr><td colSpan={6} className="px-4 py-8 text-center text-zinc-500" data-testid="onboarding-links-loading">
                Loading…
              </td></tr>
            )}
            {!loading && error && (
              <tr><td colSpan={6} className="px-4 py-8 text-center text-rose-300" data-testid="onboarding-links-error">
                {error}
              </td></tr>
            )}
            {!loading && !error && filtered.length === 0 && (
              <tr><td colSpan={6} className="px-4 py-12 text-center text-zinc-500" data-testid="onboarding-links-empty">
                No onboarding links {statusFilter !== "all" ? `with status "${statusFilter}"` : "yet"}. Click "New link" to mint one.
              </td></tr>
            )}
            {!loading && !error && filtered.map((l) => (
              <tr key={l.magic_link_url} className="border-t border-white/[0.04] hover:bg-white/[0.02] transition-colors" data-testid={`onboarding-link-row-${l.merchant_id}`}>
                <td className="px-4 py-3.5">
                  <div className="font-medium text-white">{l.legal_name || <span className="text-zinc-500">—</span>}</div>
                  {l.ref && <div className="text-[11.5px] text-zinc-500 ref-mono">{l.ref}</div>}
                </td>
                <td className="px-4 py-3.5">
                  <div className="text-zinc-300">{l.primary_contact_email || "—"}</div>
                  {l.primary_contact_name && (
                    <div className="text-[11.5px] text-zinc-500">{l.primary_contact_name}{l.primary_contact_role ? ` · ${l.primary_contact_role}` : ""}</div>
                  )}
                </td>
                <td className="px-4 py-3.5">
                  <StatusPill status={l.status} isSignedUp={l.is_signed_up} />
                </td>
                <td className="px-4 py-3.5 text-zinc-400 text-[12.5px]">{fmtDate(l.created_at)}</td>
                <td className="px-4 py-3.5 text-zinc-400 text-[12.5px]">
                  {l.status === "active" ? fmtDate(l.expires_at) : <span className="text-zinc-600">—</span>}
                </td>
                <td className="px-4 py-3.5 text-right">
                  <div className="inline-flex items-center gap-1.5">
                    {l.status === "active" && (
                      <>
                        <IconBtn onClick={() => onCopy(l)} testId={`onboarding-link-copy-${l.merchant_id}`} title="Copy magic link">
                          <Copy className="h-3.5 w-3.5" />
                        </IconBtn>
                        <a
                          href={l.magic_link_url}
                          target="_blank"
                          rel="noreferrer"
                          data-testid={`onboarding-link-open-${l.merchant_id}`}
                          title="Open in new tab"
                          className="inline-flex items-center justify-center h-7 w-7 rounded-full hover:bg-white/[0.06] text-zinc-400 hover:text-white transition-colors"
                        >
                          <ExternalLink className="h-3.5 w-3.5" />
                        </a>
                      </>
                    )}
                    {!l.is_signed_up && (
                      <IconBtn
                        onClick={() => onRegenerate(l)}
                        testId={`onboarding-link-regen-${l.merchant_id}`}
                        title="Regenerate link"
                      >
                        <RefreshCw className="h-3.5 w-3.5" />
                      </IconBtn>
                    )}
                    <Link
                      to={`/admin/merchants/${l.merchant_id}`}
                      data-testid={`onboarding-link-merchant-${l.merchant_id}`}
                      className="ref-mono text-[11.5px] text-zinc-500 hover:text-white transition-colors px-2"
                    >
                      View merchant →
                    </Link>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Mint modal */}
      {showMint && (
        <MintLinkModal
          onClose={() => setShowMint(false)}
          onSuccess={(fresh) => {
            setShowMint(false);
            navigator.clipboard.writeText(fresh.magic_link_url).catch(() => {});
            showToast(`Created link for ${fresh.legal_name}. Copied to clipboard.`);
            refresh();
          }}
        />
      )}

      {/* Toast */}
      {toast && (
        <div
          data-testid="onboarding-links-toast"
          className={`fixed bottom-6 right-6 z-50 max-w-sm rounded-xl border px-4 py-3 text-[12.5px] flex items-start gap-2 shadow-2xl backdrop-blur-md ${
            toast.kind === "warn"
              ? "border-amber-400/30 bg-amber-400/[0.08] text-amber-100"
              : "border-emerald-400/30 bg-emerald-400/[0.08] text-emerald-100"
          }`}
        >
          {toast.kind === "warn"
            ? <AlertTriangle className="h-4 w-4 mt-0.5 text-amber-300 shrink-0" />
            : <CheckCircle2 className="h-4 w-4 mt-0.5 text-emerald-300 shrink-0" />}
          <div className="flex-1 leading-relaxed">{toast.message}</div>
          <button type="button" onClick={() => setToast(null)} className="opacity-70 hover:opacity-100"><X className="h-3.5 w-3.5" /></button>
        </div>
      )}
    </div>
  );
}

// ---------- Sub-components ----------

function CountCard({ label, value, accent }) {
  const accents = {
    cyan:    "border-cyan-400/20 bg-cyan-400/[0.04] text-cyan-200",
    emerald: "border-emerald-400/20 bg-emerald-400/[0.04] text-emerald-200",
    amber:   "border-amber-400/20 bg-amber-400/[0.04] text-amber-200",
    zinc:    "border-white/[0.08] bg-white/[0.02] text-zinc-300",
    none:    "border-white/[0.08] bg-white/[0.02] text-white",
  };
  return (
    <div className={`rounded-2xl border ${accents[accent || "none"]} px-5 py-4`} data-testid={`onboarding-links-count-${(label || "").toLowerCase()}`}>
      <div className="text-[10.5px] uppercase tracking-[0.22em] opacity-80">{label}</div>
      <div className="font-display mt-1.5 text-3xl tracking-tighter">{value ?? "—"}</div>
    </div>
  );
}

function StatusPill({ status, isSignedUp }) {
  const cfg = {
    active:     { label: "Active",      icon: Sparkles,       cls: "border-cyan-400/30 bg-cyan-400/[0.08] text-cyan-100" },
    used:       { label: isSignedUp ? "Onboarded" : "Used", icon: CheckCircle2, cls: "border-emerald-400/30 bg-emerald-400/[0.08] text-emerald-100" },
    expired:    { label: "Expired",     icon: Clock,          cls: "border-amber-400/30 bg-amber-400/[0.08] text-amber-100" },
    superseded: { label: "Superseded",  icon: Archive,        cls: "border-white/[0.10] bg-white/[0.04] text-zinc-300" },
  }[status] || { label: status, icon: Sparkles, cls: "border-white/[0.10] bg-white/[0.04] text-zinc-400" };
  const Icon = cfg.icon;
  return (
    <span className={`inline-flex items-center gap-1.5 rounded-full border ${cfg.cls} px-2.5 py-1 text-[11.5px]`} data-testid={`status-pill-${status}`}>
      <Icon className="h-3 w-3" /> {cfg.label}
    </span>
  );
}

function IconBtn({ onClick, testId, title, children }) {
  return (
    <button
      type="button"
      onClick={onClick}
      data-testid={testId}
      title={title}
      aria-label={title}
      className="inline-flex items-center justify-center h-7 w-7 rounded-full hover:bg-white/[0.06] text-zinc-400 hover:text-white transition-colors"
    >
      {children}
    </button>
  );
}

function fmtDate(iso) {
  if (!iso) return "—";
  try {
    const d = new Date(iso);
    return d.toLocaleString(undefined, {
      year: "numeric", month: "short", day: "numeric",
      hour: "2-digit", minute: "2-digit",
    });
  } catch {
    return iso;
  }
}

// ---------- Mint modal ----------

function MintLinkModal({ onClose, onSuccess }) {
  const [form, setForm] = useState({
    legal_name: "",
    primary_contact_email: "",
    primary_contact_name: "",
    primary_contact_role: "",
    primary_contact_phone: "",
    registration_number: "",
    notes: "",
  });
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");

  const set = (key) => (e) => setForm((f) => ({ ...f, [key]: e.target.value }));

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!form.legal_name.trim() || !form.primary_contact_email.trim()) {
      setError("Legal name and primary contact email are required.");
      return;
    }
    setSubmitting(true);
    setError("");
    try {
      const body = Object.fromEntries(
        Object.entries(form).filter(([, v]) => String(v || "").trim()),
      );
      const { data } = await api.post("/admin/merchants/stub", body);
      onSuccess({ ...data, legal_name: form.legal_name });
    } catch (err) {
      const detail = err?.response?.data?.detail;
      const msg = Array.isArray(detail) ? detail.map((d) => d.msg).join(", ") :
                  typeof detail === "string" ? detail :
                  (detail?.message) || "Could not create the link.";
      setError(msg);
      setSubmitting(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-sm px-4 py-8 overflow-y-auto"
      role="dialog"
      aria-modal="true"
      data-testid="onboarding-links-mint-modal"
    >
      <form onSubmit={onSubmit} className="w-full max-w-lg rounded-2xl border border-white/[0.08] bg-zinc-950 p-6 shadow-2xl">
        <div className="flex items-start justify-between gap-3 mb-5">
          <div>
            <div className="text-[11px] uppercase tracking-[0.22em] text-zinc-500">— New onboarding link</div>
            <h2 className="font-display mt-1.5 text-2xl tracking-tighter text-white">Mint a magic link</h2>
            <p className="mt-1.5 text-[12.5px] text-zinc-500 leading-relaxed">
              Creates a stub merchant + a 7-day single-use link. The merchant lands on a pre-filled signup form when they click.
            </p>
          </div>
          <button type="button" onClick={onClose} aria-label="Close" data-testid="onboarding-links-mint-close" className="text-zinc-500 hover:text-white"><X className="h-4 w-4" /></button>
        </div>
        <div className="space-y-4">
          <ModalField label="Legal name *" value={form.legal_name} onChange={set("legal_name")} placeholder="Acme Pay Limited" testId="mint-legal-name" required />
          <ModalField label="Primary contact email *" type="email" value={form.primary_contact_email} onChange={set("primary_contact_email")} placeholder="alice@acme.io" testId="mint-contact-email" required />
          <div className="grid grid-cols-2 gap-3">
            <ModalField label="Contact name" value={form.primary_contact_name} onChange={set("primary_contact_name")} placeholder="Alice Chen" testId="mint-contact-name" />
            <ModalField label="Role" value={form.primary_contact_role} onChange={set("primary_contact_role")} placeholder="CFO" testId="mint-contact-role" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <ModalField label="Contact phone" value={form.primary_contact_phone} onChange={set("primary_contact_phone")} placeholder="+35799123456" testId="mint-contact-phone" />
            <ModalField label="Registration #" value={form.registration_number} onChange={set("registration_number")} placeholder="HE12345" testId="mint-reg-number" />
          </div>
          <div>
            <label className="block text-[11px] uppercase tracking-[0.2em] text-zinc-500 mb-1.5">Notes (optional)</label>
            <textarea
              value={form.notes}
              onChange={set("notes")}
              rows={3}
              data-testid="mint-notes"
              placeholder="Switching from Stripe, 4-week timeline."
              className="w-full rounded-xl bg-white/[0.03] border border-white/[0.08] focus:border-[#00E5FF]/50 focus:outline-none focus:ring-2 focus:ring-[#00E5FF]/20 px-3 py-2.5 text-[13px] text-white placeholder:text-zinc-600"
            />
          </div>
        </div>
        {error && <div data-testid="onboarding-links-mint-error" className="mt-4 rounded-xl border border-rose-400/20 bg-rose-400/[0.05] text-rose-200 text-[12.5px] px-3 py-2.5">{error}</div>}
        <div className="mt-6 flex flex-wrap gap-2 justify-end">
          <button type="button" onClick={onClose} className="rounded-full bg-white/[0.06] hover:bg-white/[0.10] text-white px-4 py-2 text-[13px] transition-colors">
            Cancel
          </button>
          <button type="submit" disabled={submitting} data-testid="onboarding-links-mint-submit" className="rounded-full bg-white text-black hover:bg-zinc-200 disabled:opacity-50 px-5 py-2 text-[13px] font-medium transition-colors">
            {submitting ? "Creating…" : "Create link"}
          </button>
        </div>
      </form>
    </div>
  );
}

function ModalField({ label, type = "text", value, onChange, placeholder, testId, required }) {
  return (
    <div>
      <label className="block text-[11px] uppercase tracking-[0.2em] text-zinc-500 mb-1.5">{label}</label>
      <input
        type={type}
        value={value}
        onChange={onChange}
        placeholder={placeholder}
        required={required}
        data-testid={testId}
        className="w-full rounded-xl bg-white/[0.03] border border-white/[0.08] focus:border-[#00E5FF]/50 focus:outline-none focus:ring-2 focus:ring-[#00E5FF]/20 px-3 py-2.5 text-[13px] text-white placeholder:text-zinc-600"
      />
    </div>
  );
}
